import React, { useCallback } from 'react';

const SourceSelector = React.memo(({ value, onChange, sourceConfig, itemId }) => {
  // Déterminer si on utilise toutes les sources (pour la compatibilité avec les données existantes)
  const useAllSources = value === 'all';

  // Préparer la structure des workspaces et channels sélectionnés
  const selectedWorkspaces = React.useMemo(() => {
    const workspacesMap = {};

    if (sourceConfig && sourceConfig.workspaces) {
      // Initialiser tous les workspaces et channels
      // Adapter pour la nouvelle structure d'objet
      Object.entries(sourceConfig.workspaces).forEach(([workspaceName, workspace]) => {
        workspacesMap[workspaceName] = {
          selected: useAllSources, // Sélectionné si on utilise toutes les sources
          channels: {}
        };

        if (workspace.channels) {
          workspace.channels.forEach(channel => {
            workspacesMap[workspaceName].channels[channel] = useAllSources; // Sélectionné si on utilise toutes les sources
          });
        }
      });

      // Si on a des sélections spécifiques, mettre à jour avec ces sélections
      if (!useAllSources && value && typeof value === 'object' && value.workspaces) {
        // Mettre à jour les sélections
        Object.entries(value.workspaces).forEach(([name, data]) => {
          if (workspacesMap[name]) {
            workspacesMap[name].selected = true;

            if (data.channels) {
              data.channels.forEach(channel => {
                if (workspacesMap[name].channels[channel] !== undefined) {
                  workspacesMap[name].channels[channel] = true;
                }
              });
            }
          }
        });
      }
    }

    return workspacesMap;
  }, [value, sourceConfig, useAllSources]);

  // Gérer le changement de sélection pour un workspace
  const handleWorkspaceChange = useCallback((workspaceName, checked) => {
    // Créer une copie de la valeur actuelle
    let newWorkspaces = {};

    // Partir de la valeur actuelle ou créer une nouvelle structure
    if (useAllSources) {
      // Si on utilisait toutes les sources, créer une nouvelle structure
      Object.entries(sourceConfig.workspaces).forEach(([name, workspace]) => {
        if (name !== workspaceName) { // Exclure le workspace qu'on est en train de modifier
          newWorkspaces[name] = {
            channels: workspace.channels || []
          };
        }
      });
    } else if (value && value.workspaces) {
      // Sinon, partir de la valeur actuelle
      newWorkspaces = { ...value.workspaces };

      // Supprimer le workspace qu'on est en train de modifier
      delete newWorkspaces[workspaceName];
    }

    // Ajouter le workspace modifié s'il est sélectionné
    if (checked) {
      // Récupérer tous les channels du workspace depuis sourceConfig
      const workspaceChannels = sourceConfig.workspaces[workspaceName]?.channels || [];

      newWorkspaces[workspaceName] = {
        channels: workspaceChannels
      };
    }

    onChange({ workspaces: newWorkspaces });
  }, [onChange, sourceConfig, value, useAllSources]);

  // Gérer le changement de sélection pour un channel
  const handleChannelChange = useCallback((workspaceName, channelName, checked) => {
    // Créer une copie de la valeur actuelle
    let newWorkspaces = {};

    // Partir de la valeur actuelle ou créer une nouvelle structure
    if (useAllSources) {
      // Si on utilisait toutes les sources, créer une nouvelle structure
      Object.entries(sourceConfig.workspaces).forEach(([name, workspace]) => {
        newWorkspaces[name] = {
          channels: [...(workspace.channels || [])]
        };
      });
    } else if (value && value.workspaces) {
      // Sinon, partir de la valeur actuelle
      newWorkspaces = JSON.parse(JSON.stringify(value.workspaces)); // Deep clone
    }

    // S'assurer que le workspace existe
    if (!newWorkspaces[workspaceName]) {
      newWorkspaces[workspaceName] = { channels: [] };
    }

    // Mettre à jour les channels du workspace
    let channels = [...(newWorkspaces[workspaceName].channels || [])];

    if (checked) {
      // Ajouter le channel s'il n'est pas déjà présent
      if (!channels.includes(channelName)) {
        channels.push(channelName);
      }
    } else {
      // Retirer le channel s'il est présent
      channels = channels.filter(c => c !== channelName);
    }

    // Mettre à jour les channels
    newWorkspaces[workspaceName].channels = channels;

    onChange({ workspaces: newWorkspaces });
  }, [onChange, sourceConfig, value, useAllSources]);

  // Sélectionner tous les channels d'un workspace
  const selectAllChannels = useCallback((workspaceName) => {
    // Récupérer tous les channels du workspace depuis sourceConfig
    const workspaceChannels = sourceConfig.workspaces[workspaceName]?.channels || [];

    // Créer une copie de la valeur actuelle
    let newWorkspaces = {};

    // Partir de la valeur actuelle ou créer une nouvelle structure
    if (useAllSources) {
      // Si on utilisait toutes les sources, créer une nouvelle structure
      Object.entries(sourceConfig.workspaces).forEach(([name, workspace]) => {
        newWorkspaces[name] = {
          channels: [...(workspace.channels || [])]
        };
      });
    } else if (value && value.workspaces) {
      // Sinon, partir de la valeur actuelle
      newWorkspaces = JSON.parse(JSON.stringify(value.workspaces)); // Deep clone
    }

    // Mettre à jour le workspace avec tous ses channels
    newWorkspaces[workspaceName] = {
      channels: [...workspaceChannels]
    };

    onChange({ workspaces: newWorkspaces });
  }, [onChange, sourceConfig, value, useAllSources]);

  // Désélectionner tous les channels d'un workspace
  const deselectAllChannels = useCallback((workspaceName) => {
    // Créer une copie de la valeur actuelle
    let newWorkspaces = {};

    // Partir de la valeur actuelle ou créer une nouvelle structure
    if (useAllSources) {
      // Si on utilisait toutes les sources, créer une nouvelle structure
      Object.entries(sourceConfig.workspaces).forEach(([name, workspace]) => {
        if (name !== workspaceName) { // Exclure le workspace qu'on est en train de modifier
          newWorkspaces[name] = {
            channels: [...(workspace.channels || [])]
          };
        }
      });
    } else if (value && value.workspaces) {
      // Sinon, partir de la valeur actuelle
      newWorkspaces = JSON.parse(JSON.stringify(value.workspaces)); // Deep clone

      // Supprimer le workspace qu'on est en train de modifier
      delete newWorkspaces[workspaceName];
    }

    // Ajouter le workspace avec un tableau de channels vide
    newWorkspaces[workspaceName] = {
      channels: []
    };

    onChange({ workspaces: newWorkspaces });
  }, [onChange, sourceConfig, value, useAllSources]);

  // Sélectionner tous les workspaces et channels
  const selectAll = useCallback(() => {
    const workspaces = {};
    Object.entries(sourceConfig.workspaces).forEach(([name, workspace]) => {
      workspaces[name] = {
        channels: [...(workspace.channels || [])]
      };
    });

    onChange({ workspaces });
  }, [onChange, sourceConfig]);

  // Désélectionner tous les workspaces et channels
  const deselectAll = useCallback(() => {
    const workspaces = {};
    Object.entries(sourceConfig.workspaces).forEach(([name, workspace]) => {
      workspaces[name] = {
        channels: []
      };
    });

    onChange({ workspaces });
  }, [onChange, sourceConfig]);

  // Source configuration unavailable
  if (!sourceConfig || !sourceConfig.workspaces) {
    return (
      <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200">
        Configuration de la source indisponible : vérifiez qu'elle est correctement renseignée.
      </p>
    );
  }

  // 'all' is converted once into the explicit list of workspaces and channels.
  if (useAllSources) {
    const workspaces = {};
    Object.entries(sourceConfig.workspaces).forEach(([name, workspace]) => {
      workspaces[name] = {
        channels: [...(workspace.channels || [])]
      };
    });

    setTimeout(() => {
      onChange({ workspaces });
    }, 0);
  }

  const linkButton = 'cursor-pointer rounded-md px-2 py-1 text-xs font-medium outline-none focus-visible:ring-2 focus-visible:ring-brand-500';

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-end gap-1">
        <button type="button" onClick={selectAll} className={`${linkButton} text-brand-600 hover:bg-brand-50 dark:text-brand-400 dark:hover:bg-brand-950`}>
          Tout sélectionner
        </button>
        <button type="button" onClick={deselectAll} className={`${linkButton} text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-white/5`}>
          Tout désélectionner
        </button>
      </div>

      <div className="max-h-80 space-y-4 overflow-y-auto rounded-xl border border-gray-200 bg-gray-50/60 p-4 dark:border-gray-800 dark:bg-night-850">
        {Object.entries(sourceConfig.workspaces).map(([workspaceName, workspace], wIndex) => {
          const workspaceState = selectedWorkspaces[workspaceName] || { selected: false, channels: {} };
          const channelValues = Object.values(workspaceState.channels);
          const allChannelsSelected = channelValues.length > 0 && channelValues.every(val => val);
          const someChannelsSelected = channelValues.some(val => val);
          const indeterminate = someChannelsSelected && !allChannelsSelected;
          const channels = workspace.channels || [];

          return (
            <div key={wIndex}>
              <div className="mb-2 flex items-center gap-2">
                <input
                  type="checkbox"
                  id={`workspace-${itemId}-${wIndex}`}
                  checked={allChannelsSelected || workspaceState.selected}
                  ref={el => {
                    if (el) {
                      el.indeterminate = indeterminate;
                    }
                  }}
                  onChange={(e) => handleWorkspaceChange(workspaceName, e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                />
                <label htmlFor={`workspace-${itemId}-${wIndex}`} className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                  {workspaceName}
                </label>
                {channels.length > 0 && (
                  <span className="ml-auto flex gap-1">
                    <button type="button" onClick={() => selectAllChannels(workspaceName)} className={`${linkButton} text-brand-600 hover:bg-brand-50 dark:text-brand-400 dark:hover:bg-brand-950`}>
                      Tous
                    </button>
                    <button type="button" onClick={() => deselectAllChannels(workspaceName)} className={`${linkButton} text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-white/5`}>
                      Aucun
                    </button>
                  </span>
                )}
              </div>

              {channels.length > 0 && (
                <div className="ml-6 grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {channels.map((channel, cIndex) => (
                    <label key={cIndex} htmlFor={`channel-${itemId}-${wIndex}-${cIndex}`} className="flex min-w-0 cursor-pointer items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <input
                        type="checkbox"
                        id={`channel-${itemId}-${wIndex}-${cIndex}`}
                        checked={workspaceState.channels[channel] || false}
                        onChange={(e) => handleChannelChange(workspaceName, channel, e.target.checked)}
                        className="h-3.5 w-3.5 flex-shrink-0 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                      />
                      <span className="truncate">#{channel}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});

export default SourceSelector;